/**
 * sportConfig.js — Client-side sport configuration (mirrors server/services/sportConfig.js)
 *
 * Each sport defines:
 *   label          — Display name
 *   scoreLabel     — What to call the score input (e.g. "Goals", "Points", "Runs")
 *   pointsForWin   — Standings points awarded for a win
 *   pointsForTie   — Standings points awarded for a tie
 *   pointsForLoss  — Standings points awarded for a loss
 *   allowTies      — Whether ties are a valid game result
 *   statLabels     — Short labels for stat columns { goalsFor, goalsAgainst, goalDifferential }
 */

const sportConfigs = {
  general: {
    label: 'General',
    scoreLabel: 'Score',
    officialTitle: 'Referee',
    pointsForWin: 2,
    pointsForTie: 1,
    pointsForLoss: 0,
    allowTies: true,
    statLabels: { goalsFor: 'PF', goalsAgainst: 'PA', goalDifferential: 'Diff' },
  },
  soccer: {
    label: 'Soccer',
    scoreLabel: 'Goals',
    officialTitle: 'Referee',
    pointsForWin: 3,
    pointsForTie: 1,
    pointsForLoss: 0,
    allowTies: true,
    statLabels: { goalsFor: 'GF', goalsAgainst: 'GA', goalDifferential: 'GD' },
  },
  basketball: {
    label: 'Basketball',
    scoreLabel: 'Points',
    officialTitle: 'Referee',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    statLabels: { goalsFor: 'PF', goalsAgainst: 'PA', goalDifferential: 'Diff' },
  },
  volleyball: {
    label: 'Volleyball',
    scoreLabel: 'Sets',
    officialTitle: 'Referee',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    statLabels: { goalsFor: 'SW', goalsAgainst: 'SL', goalDifferential: 'Diff' },
  },
  softball: {
    label: 'Softball',
    scoreLabel: 'Runs',
    officialTitle: 'Umpire',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    statLabels: { goalsFor: 'RF', goalsAgainst: 'RA', goalDifferential: 'Diff' },
  },
  hockey: {
    label: 'Hockey',
    scoreLabel: 'Goals',
    officialTitle: 'Referee',
    pointsForWin: 3,
    pointsForTie: 1,
    pointsForLoss: 0,
    allowTies: true,
    statLabels: { goalsFor: 'GF', goalsAgainst: 'GA', goalDifferential: 'GD' },
  },
  football: {
    label: 'Football',
    scoreLabel: 'Points',
    officialTitle: 'Official',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    statLabels: { goalsFor: 'PF', goalsAgainst: 'PA', goalDifferential: 'Diff' },
  },
  winloss: {
    label: 'Win/Loss Only',
    scoreLabel: 'Result',
    officialTitle: 'Referee',
    pointsForWin: 2,
    pointsForTie: 1,
    pointsForLoss: 0,
    allowTies: true,
    winLossOnly: true,
    statLabels: {},
  },
  pool: {
    label: 'Pool',
    scoreLabel: 'Racks',
    officialTitle: 'Referee',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    supportsBestOf: true,
    statLabels: { goalsFor: 'RW', goalsAgainst: 'RL', goalDifferential: 'Diff' },
  },
  darts: {
    label: 'Darts',
    scoreLabel: 'Legs',
    officialTitle: 'Referee',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    supportsBestOf: true,
    statLabels: { goalsFor: 'LW', goalsAgainst: 'LL', goalDifferential: 'Diff' },
  },
  pickleball: {
    label: 'Pickleball',
    scoreLabel: 'Points',
    officialTitle: 'Referee',
    pointsForWin: 1,
    pointsForTie: 0,
    pointsForLoss: 0,
    allowTies: false,
    supportsPlayerRatings: true,
    statLabels: { goalsFor: 'PF', goalsAgainst: 'PA', goalDifferential: 'Diff' },
  },
};

// DB-backed sport configs fetched from API (overrides hardcoded)
let dbSportConfigs = null;
let dbConfigsLoading = false;
let dbConfigsLoaded = false;

/**
 * Load sport configs from the API and merge with hardcoded.
 * Called once on app initialization. Falls back to hardcoded on failure.
 */
export async function loadSportConfigsFromAPI() {
  if (dbConfigsLoading || dbConfigsLoaded) return;
  dbConfigsLoading = true;
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${API_BASE_URL}/sports`);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.sports) {
        dbSportConfigs = data.sports;
        dbConfigsLoaded = true;
      }
    }
  } catch (e) {
    // API not available — fall back to hardcoded
  } finally {
    dbConfigsLoading = false;
  }
}

/**
 * Invalidate the client-side sport config cache (force re-fetch on next load).
 */
export function invalidateSportConfigCache() {
  dbSportConfigs = null;
  dbConfigsLoaded = false;
}

/**
 * Get sport configuration, falling back to 'general' for unknown sports.
 * Uses DB-backed configs when available.
 */
export function getSportConfig(sportName) {
  if (dbSportConfigs) {
    return dbSportConfigs[sportName] || dbSportConfigs.general || sportConfigs[sportName] || sportConfigs.general;
  }
  return sportConfigs[sportName] || sportConfigs.general;
}

/**
 * Get all available sport configs (merged DB + hardcoded).
 * Returns an object keyed by sport key.
 */
export function getAllSportConfigs() {
  if (dbSportConfigs) {
    return { ...sportConfigs, ...dbSportConfigs };
  }
  return sportConfigs;
}

/**
 * Get the sport-appropriate title for a game official.
 * E.g., "Umpire" for softball, "Official" for football, "Referee" for most sports.
 */
export function getOfficialTitle(sportName) {
  return getSportConfig(sportName).officialTitle || 'Referee';
}
